import React from 'react'
import { router, usePage } from '@inertiajs/react'
import {
  Box, Button, Paper, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'
import StatusBadge from '../../../../components/ui/StatusBadge'
import FormattedNumber from '../../../../components/ui/FormattedNumber'

Show.layout = (page) => <AppShell>{page}</AppShell>

export default function Show({ property, distribution_key, shares = [], components = [] }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const basePath = `/m/${tenant.slug}/properties/${property.id}/distribution_keys`

  const handleDelete = () => {
    router.delete(`${basePath}/${distribution_key.id}`)
  }

  const totalShares = shares.reduce((sum, s) => sum + parseFloat(s.share || 0), 0)

  return (
    <Box>
      <PageHeader
        title={distribution_key.name}
        breadcrumbs={[
          { label: 'Verteilerschlüssel', href: basePath },
          { label: distribution_key.name },
        ]}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<EditIcon />}
              onClick={() => router.visit(`${basePath}/${distribution_key.id}/edit`)}
              data-testid="edit-btn">
              Bearbeiten
            </Button>
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />}
              onClick={handleDelete}
              data-testid="delete-btn">
              Löschen
            </Button>
          </Box>
        }
      />

      {/* Summary card */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Typ</Typography>
            <StatusBadge type="distribution_key_type" value={distribution_key.key_type} />
          </Box>
          {distribution_key.attribute_name && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Attribut</Typography>
              <Typography variant="body2" fontWeight={500}>{distribution_key.attribute_name}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Einheiten</Typography>
            <Typography variant="body2" fontWeight={500}>{shares.length}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Composite components */}
      {components.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>Komponenten</Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Attribut</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Gewichtung (%)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {components.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.attribute_name}</TableCell>
                    <TableCell align="right"><FormattedNumber value={c.weight} decimals={1} percent /></TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                  <TableCell><Typography variant="body2" fontWeight={600}>Summe</Typography></TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>
                      <FormattedNumber value={components.reduce((sum, c) => sum + parseFloat(c.weight || 0), 0)} decimals={1} percent />
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Shares table */}
      <Typography variant="h6" fontWeight={600} gutterBottom>Anteile</Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.subtle' }}>
              <TableCell sx={{ fontWeight: 600 }}>Einheit</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Anteil</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Prozent</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shares.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">Keine Anteile vorhanden.</Typography>
                </TableCell>
              </TableRow>
            )}
            {shares.map((s) => (
              <TableRow key={s.unit_id}>
                <TableCell><Typography variant="body2" fontWeight={500}>{s.unit_number}</Typography></TableCell>
                <TableCell align="right"><FormattedNumber value={s.share} decimals={6} /></TableCell>
                <TableCell align="right"><FormattedNumber value={s.percentage} decimals={2} percent /></TableCell>
              </TableRow>
            ))}
            {shares.length > 0 && (
              <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                <TableCell><Typography variant="body2" fontWeight={600}>Summe</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight={600}><FormattedNumber value={totalShares} decimals={6} /></Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight={600}><FormattedNumber value={100} decimals={2} percent /></Typography></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
